import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { formatFileSize } from '../../utils/formatters';
import { uploadPDF } from '../../services/api';

const FileUploader = ({ onFilesUploaded, loading, setLoading, compact = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Listen for files dropped anywhere on the page
  useEffect(() => {
    const handleFilesDropped = (event) => {
      const files = event.detail;
      if (files && files.length > 0) {
        processFiles(files);
      }
    };

    window.addEventListener('filesDropped', handleFilesDropped);
    return () => window.removeEventListener('filesDropped', handleFilesDropped);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFiles = async (files) => {
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      alert('Please upload PDF files only');
      return;
    }

    setLoading(true);
    const processedFiles = [];
    
    for (const file of pdfFiles) {
      try {
        const result = await uploadPDF(file);
        const fileData = {
          name: file.name,
          size: file.size,
          status: 'success',
          result: result.data
        };
        processedFiles.push(fileData);
        setUploadedFiles(prev => [...prev, fileData]);
      } catch (error) {
        const fileData = {
          name: file.name,
          size: file.size,
          status: 'error',
          error: error.message
        };
        processedFiles.push(fileData);
        setUploadedFiles(prev => [...prev, fileData]);
      }
    }
    
    setLoading(false);
    
    if (onFilesUploaded) {
      onFilesUploaded(processedFiles);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [onFilesUploaded, setLoading]);

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files);
    await processFiles(files);
  };

  // Compact mode: just a button
  if (compact) {
    return (
      <div>
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload-compact"
        />
        <label
          htmlFor="file-upload-compact"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 cursor-pointer whitespace-nowrap"
        >
          <Upload className="h-4 w-4 mr-2" />
          Select Files to Upload
        </label>
        {loading && (
          <span className="ml-3 text-sm text-gray-600">Processing...</span>
        )}
      </div>
    );
  }

  // Full mode: drag and drop area
  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-6">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Upload Blood Test PDFs
        </h3>
        <p className="text-gray-600 mb-4">
          Drag and drop your PDF files here, or click to browse
        </p>
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
        >
          Choose Files
        </label>
      </div>

      {loading && (
        <div className="mt-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Processing PDFs...</p>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Recently Uploaded Files
          </h4>
          <div className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  {file.status === 'success' ? (
                    <div className="flex items-center text-green-600">
                      <div className="h-2 w-2 bg-green-600 rounded-full mr-2"></div>
                      <span className="text-xs">Success</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      <span className="text-xs">Error</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;